import React from "react";
import { Button, Paper, styled, Typography } from "@mui/material";
import MyAccessImage from "../../assets/myaccess.svg";
import GoogleDocsImage from "../../assets/googledocs.svg";
import MicrosoftWordImage from "../../assets/microsoft-365-word.svg";
import IntelliWriterImage from "../../assets/intelliwriter.svg";
import BackGroundImage from "../../assets/background.svg";
import { FEEDBACK_URL } from "../../utils/constants";
import { useSidebar } from "../../contexts/SidebarContext";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  textAlign: "center",
  color: theme.palette.text.secondary,
  height: "200px",
  flex: "1",
  minWidth: "200px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2),
  boxShadow: "none",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "#f9fafb",
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "#1A2027",
  }),
}));

const LogoContainer = styled("div")({
  flex: "1",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const TextContainer = styled("div")({
  textAlign: "center",
});

const SurveyPaper = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  backgroundColor: "#fff",
  padding: "6rem",
  backgroundImage: `url(${BackGroundImage})`,
  backgroundSize: "cover",
  borderRadius: "6px",
  boxShadow: "none",
}));

const OnboardingImg = styled("img")({
  width: "150px",
  height: "150px",
  objectFit: "contain",
  display: "inline-block",
});

const usagePlacesList = [
  {
    name: "Google Docs",
    icon: <OnboardingImg src={GoogleDocsImage} alt="Google Docs" />,
    iconlabel: "Click to launch Google Docs",
    url: "https://writeahead.com/instruction/#googledocs",
    applicationurl: "https://docs.google.com/document/",
  },
  {
    name: "Microsoft Word",
    icon: <OnboardingImg src={MicrosoftWordImage} alt="Microsoft Word" />,
    iconlabel: "Click to launch Microsoft Word",
    url: "https://writeahead.com/instruction/#microsoftword",
    applicationurl: "https://office.live.com/start/Word.aspx",
  },
  {
    name: "My Access",
    icon: <OnboardingImg src={MyAccessImage} alt="My Access" />,
    iconlabel: "Click to launch MY Access!",
    url: "https://writeahead.com/instruction/#myaccess",
    applicationurl: "https://www.myaccess.com",
  },
  {
    name: "IntelliWriter",
    icon: <OnboardingImg src={IntelliWriterImage} alt="IntelliWriter" />,
    iconlabel: "Click to launch IntelliWriter",
    url: "https://writeahead.com/instruction/#intelliwriter",
    applicationurl: "https://www.intelliwriter.com/",
  },
];

const WriteAheadUsagePlacePresentPage: React.FC = () => {
  const { isCollapsed } = useSidebar();
  const productRedirectionHandler = (url: string) => {
    window.open(url, "_blank");
  };
  return (
    <>
      <div style={{ marginBottom: "1rem", maxWidth: "70vw" }}>
        <Typography
          style={{
            fontFamily: "Poppins",
            fontWeight: "bold",
            color: "#161616",
            marginTop: "1em",
            marginBottom: "1rem",
          }}
          variant="h4"
          component="div"
        >
          Where to use WriteAhead
        </Typography>
        <Typography
          style={{
            fontFamily: "Poppins",
            color: "#5a5a5a",
            marginBottom: "0.8rem",
            lineHeight: "1.6",
          }}
          variant="subtitle1"
          component="div"
        >
          Click on any icon below - Google Docs, Microsoft Word, MY Access, or
          IntelliWriter - to seamlessly launch the WriteAhead extension and
          generate AI-powered feedback for enhanced student writing outcomes.
          Users of Clever, ClassLink, Canvas, or D2L should launch into MY
          Access or IntelliWriter through their institution or district portal
          to use the WriteAhead extension.
        </Typography>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          marginBottom: "2rem",
        }}
      >
        {usagePlacesList.map((p) => (
          <div
            key={p.name}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: "1",
              minWidth: "200px",
            }}
          >
            <Item
              className="block-link"
              onClick={() => productRedirectionHandler(p.applicationurl)}
            >
              <LogoContainer>{p.icon}</LogoContainer>
              <TextContainer>
                <span
                  style={{
                    fontWeight: "700",
                  }}
                >
                  {p.iconlabel}
                </span>
              </TextContainer>
            </Item>
            <Button
              className="primary"
              style={{
                fontSize: "large",
                fontWeight: "bold",
                lineHeight: "1rem",
                padding: "1rem 2rem",
                marginTop: "1rem",
              }}
              onClick={() => productRedirectionHandler(p.url)}
            >
              Learn More
            </Button>
          </div>
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          marginLeft: isCollapsed ? "60px" : "250px",
          marginTop: "2rem",
          transition: "margin-left 0.3s ease",
        }}
      >
        <SurveyPaper style={{ borderRadius: 0, margin: 0, width: "100%" }}>
          <Typography
            style={{
              fontFamily: "Poppins",
              fontWeight: "bold",
              color: "#161616",
              fontSize: "2rem",
              marginBottom: "1rem",
            }}
            variant="h3"
            component="div"
            align="center"
          >
            We value your feedback!
          </Typography>
          <Typography
            style={{
              fontFamily: "Poppins",
              color: "#5a5a5a",
              marginBottom: "1em",
            }}
            variant="subtitle1"
            component="div"
            align="center"
          >
            Whether you have been enjoying your experience with WriteAhead or
            have suggestions for improvement, we would love to hear from you.
            Please take a moment to share your thoughts by completing our
            customer survey:
          </Typography>
          <Typography
            style={{ fontFamily: "Poppins", color: "#5a5a5a" }}
            variant="subtitle1"
            component="div"
            align="center"
          >
            <Button
              onClick={() => productRedirectionHandler(FEEDBACK_URL)}
              style={{
                backgroundColor: "#b300b3",
                color: "white",
                fontSize: "large",
                fontWeight: "bold",
                fontFamily: "Poppins",
                textTransform: "capitalize",
                lineHeight: "1rem",
                padding: "1rem 2rem",
              }}
            >
              Take the Survey
            </Button>
          </Typography>
        </SurveyPaper>
      </div>
    </>
  );
};

export default WriteAheadUsagePlacePresentPage;
